// API chat khách ↔ admin. Hai nửa dùng hai kênh auth khác nhau:
//   - Nửa khách (public/*): cookie customer_session — fetch trần
//     credentials:"include", KHÔNG đi qua openapi-fetch client của staff.
//   - Nửa admin (/customer-chats): phiên staff — api client + postFormAuthed.
// Realtime cả hai nửa nhận qua WebSocket (xem useChatSocket) — REST là kênh
// gửi + nguồn sự thật khi resync.
import { api, apiBaseUrl } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import type { components } from "@/lib/api/generated/schema";

export type ChatMessage = components["schemas"]["ChatMessage"];
export type ChatSession = components["schemas"]["ChatSession"];
/** Phiên nhìn từ màn nhân viên — mang thêm cơ sở, dấu chưa đọc riêng, ai đã đọc. */
export type ChatAdminSession = components["schemas"]["ChatAdminSession"];
/**
 * MỘT DÒNG MỖI KHÁCH ở hòm thư nhân viên (quyết định 0065 mục 3). Các luồng
 * theo đơn nằm trong `threads` và về CÙNG lượt gọi — bấm mở ra là có ngay,
 * không chờ mạng. Khách 5 buổi chụp vẫn chỉ chiếm một dòng.
 */
export type ChatAdminCustomer = components["schemas"]["ChatAdminCustomer"];
/** Một luồng ở phía KHÁCH. `id` rỗng = luồng chưa tồn tại, gửi kèm orderId để tạo. */
export type ChatCustomerThread = components["schemas"]["ChatCustomerThread"];
/** Thẻ phân loại dùng chung cả công ty (quyết định 0135); gắn được cho luồng lẫn khách. */
export type ChatTag = components["schemas"]["ChatTag"];
export type ChatTagInput = components["schemas"]["ChatTagInput"];

// ---- chat khách đợt 1 (QĐ 0223) ----------------------------------------------
/** Người đang dùng hộp thư: ghi được không, điều phối được không, socket có đẩy tin không. */
export type ChatMe = components["schemas"]["ChatMe"];
/** Hội thoại mức KHÁCH: người phụ trách, trạng thái, ưu tiên — mọi luồng mang cùng khối. */
export type ChatConversation = components["schemas"]["ChatConversation"];
export type ChatConversationStatus = ChatConversation["status"];
export type ChatPriority = ChatConversation["priority"];
/** Kết quả nhận / giao / đổi trạng thái / ưu tiên: khối hội thoại mới + dòng hoạt động (nếu có đổi). */
export type ChatConversationChange = components["schemas"]["ChatConversationChange"];
/** Số trên ba thẻ + ô "Chưa ai trả lời" + ô "Nhắc tôi" + ô "Khách mới" của hộp thư. */
export type ChatCounts = components["schemas"]["ChatCounts"];
export type ChatAssignableStaff = components["schemas"]["ChatAssignableStaff"];
/** Số KHÁCH đang chờ trả lời — con số trên mục "Chat khách" của menu (QĐ 0271). */
export type ChatWaitingCount = components["schemas"]["ChatWaitingCount"];
/** Một đơn của khách đang chat, dạng tóm tắt KHÔNG tiền (QĐ 0271). */
export type ChatCustomerOrder = components["schemas"]["ChatCustomerOrder"];
export type ChatCannedReply = components["schemas"]["ChatCannedReply"];
export type ChatCannedReplyInput = components["schemas"]["ChatCannedReplyInput"];
export type ChatBusinessHours = components["schemas"]["ChatBusinessHours"];
export type ChatBusinessHoursInput = components["schemas"]["ChatBusinessHoursInput"];
export type ChatAppearance = components["schemas"]["ChatAppearance"];
export type ChatAppearanceAdmin = components["schemas"]["ChatAppearanceAdmin"];
export type ChatDayHours = components["schemas"]["ChatDayHours"];
/** Loại tin: tin với khách · ghi chú nội bộ · dòng hoạt động · tự trả lời ngoài giờ. */
export type ChatMessageKind = ChatMessage["kind"];
/** Tin cuối của luồng / khách — dòng xem trước trên thẻ. */
export type ChatLastMessage = components["schemas"]["ChatLastMessage"];

export type ChatTab = "all" | "mine" | "unassigned";
export type ChatStatusFilter = "any" | ChatConversationStatus;
/**
 * Bộ lọc hộp thư. `mentions` bật thì máy chủ vẫn xét tab + trạng thái, nên giao
 * diện gửi kèm `tab=all&status=any` (xem `listQuery`) — "Nhắc tôi" là mọi khách
 * có lời nhắc mình chưa xem, bất kể thẻ nào đang chọn.
 */
/** Nhóm của cột danh sách (CEO chốt 3b 15/10/2026): "Cần trả lời" / "Đang chờ khách". */
export type ChatTurn = "ours" | "theirs";

export type ChatListFilters = {
  tag: string | null;
  tab: ChatTab;
  status: ChatStatusFilter;
  unanswered: boolean;
  mentions: boolean;
  /** Chỉ khách mới: chưa có đơn nào, chưa đặt lịch lần nào (CEO chốt 1a · 2a, QĐ 0340). */
  newCustomer: boolean;
  /** Nhóm hội thoại; bỏ trống = không chia nhóm. Nhóm "Đã xong" dùng `status: "resolved"`. */
  turn?: ChatTurn | null | undefined;
};

/**
 * Trỏ tới luồng cần thao tác. Bỏ trống cả hai = luồng "Hỏi chung" — đúng hành
 * vi trước migration 0185, nên chỗ gọi cũ không phải sửa gì.
 *
 * Vì sao có HAI trường: đơn khách chưa từng nhắn thì luồng chưa tồn tại, chưa
 * có `threadId` để mà gửi. Cách còn lại là đẻ sẵn luồng rỗng cho mọi đơn —
 * hòm thư nhân viên sẽ đầy luồng không ai nhắn.
 */
export type ChatThreadRef = { threadId?: string; orderId?: string };

function threadQuery(ref?: ChatThreadRef): string {
  const params = new URLSearchParams();
  if (ref?.threadId) params.set("threadId", ref.threadId);
  if (ref?.orderId) params.set("orderId", ref.orderId);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function threadBody(ref?: ChatThreadRef): Record<string, string> {
  return {
    ...(ref?.threadId ? { threadId: ref.threadId } : {}),
    ...(ref?.orderId ? { orderId: ref.orderId } : {}),
  };
}
/** Danh tính người gửi hiện trên bong bóng (tên + chức vụ + ảnh). */
export type ChatSender = components["schemas"]["ChatSender"];
export type ChatPartnerSession = components["schemas"]["ChatPartnerSession"];
/** Chủ đề tin (migration 0125). "affiliate" chỉ nhận khi SĐT là người giới thiệu. */
export type ChatTopic = NonNullable<ChatMessage["topic"]>;

export type ChatSocketEvent =
  | { type: "message"; message: ChatMessage }
  | { type: "session"; session: ChatAdminSession }
  | { type: "presence"; sessionId: string; viewers: ChatSender[] }
  // Tin vừa đổi (thu hồi, QĐ 0223 §E) — bản ĐÃ CHE như người thường thấy;
  // người điều phối tải lại lịch sử để xem bản gốc.
  | { type: "message_updated"; message: ChatMessage };

/** Trần ảnh chat — khớp CHECK 5MB ở backend/migration 0085. */
export const CHAT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const CHAT_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Gốc ws:// của kênh chat.
 *
 * Bản dựng cho tên miền đặt lịch riêng đặt VITE_API_URL = "/api/v1" (đường dẫn
 * TƯƠNG ĐỐI). `replace(/^http/)` không khớp chuỗi đó, mà WebSocket thì KHÔNG
 * nhận địa chỉ tương đối — chat khách chết câm ngay khi lên tên miền đó, dù ở
 * máy dev (địa chỉ tuyệt đối) vẫn chạy ngon. Nên ghép gốc của trang trước.
 *
 * Hai tham số chỉ để bài kiểm dựng được tình huống; nơi gọi thật không truyền.
 */
export function wsBaseUrl(
  configuredBaseUrl = apiBaseUrl,
  browserOrigin = typeof window === "undefined" ? "http://localhost" : window.location.origin,
): string {
  if (/^https?:/i.test(configuredBaseUrl)) return configuredBaseUrl.replace(/^http/, "ws");
  return new URL(configuredBaseUrl, browserOrigin).toString().replace(/\/+$/, "").replace(/^http/, "ws");
}

// ---- nửa khách (portal /booking/me) -----------------------------------------

async function publicJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      // giữ fallback
    }
    let message = serverErrorMessage(body, fallback);
    if (response.status === 429) message = "Bạn gửi tin hơi nhanh — chờ một lát rồi thử lại.";
    throw new Error(message);
  }
  return (await response.json()) as T;
}

/**
 * Các luồng khách mở được: "Hỏi chung" LUÔN đứng đầu, rồi tới từng buổi chụp.
 * Đơn chưa nhắn lần nào vẫn có mặt với `id` rỗng.
 */
export async function fetchCustomerChatThreads(input?: { signal?: AbortSignal }): Promise<ChatCustomerThread[]> {
  const init: RequestInit = { credentials: "include" };
  if (input?.signal) init.signal = input.signal;
  const response = await fetch(`${apiBaseUrl}/public/customer-chat/threads`, init);
  const body = await publicJson<{ items?: ChatCustomerThread[] }>(response, "Không tải được danh sách cuộc trò chuyện");
  return body.items ?? [];
}

export async function fetchCustomerChatMessages(
  input?: { signal?: AbortSignal; thread?: ChatThreadRef },
): Promise<ChatMessage[]> {
  const init: RequestInit = { credentials: "include" };
  if (input?.signal) init.signal = input.signal;
  const response = await fetch(
    `${apiBaseUrl}/public/customer-chat/messages${threadQuery(input?.thread)}`,
    init,
  );
  const body = await publicJson<{ items?: ChatMessage[] }>(response, "Không tải được tin nhắn");
  return body.items ?? [];
}

/**
 * Khách báo đã mở màn chat.
 *
 * CỐ TÌNH không trả gì và không ném lỗi: mọi mốc "đã xem" là thông tin MỘT
 * CHIỀU dành cho nhân viên (quyết định 0063 mục 4). Trả mốc ngược về đây là mở
 * đúng cái cửa mà quyết định đó đóng.
 */
export async function markCustomerChatRead(thread?: ChatThreadRef): Promise<void> {
  await fetch(`${apiBaseUrl}/public/customer-chat/read`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(threadBody(thread)),
  });
}

export async function sendCustomerChatMessage(
  body: string,
  topic: ChatTopic = "general",
  replyToId?: string,
  thread?: ChatThreadRef,
): Promise<ChatMessage> {
  const response = await fetch(`${apiBaseUrl}/public/customer-chat/messages`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body, topic, ...(replyToId ? { replyToId } : {}), ...threadBody(thread) }),
  });
  return publicJson<ChatMessage>(response, "Không gửi được tin nhắn");
}

export async function sendCustomerChatImage(
  file: File,
  caption?: string,
  topic: ChatTopic = "general",
  replyToId?: string,
  thread?: ChatThreadRef,
): Promise<ChatMessage> {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("body", caption);
  form.append("topic", topic);
  if (replyToId) form.append("replyToId", replyToId);
  if (thread?.threadId) form.append("threadId", thread.threadId);
  if (thread?.orderId) form.append("orderId", thread.orderId);
  const response = await fetch(`${apiBaseUrl}/public/customer-chat/attachments`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  return publicJson<ChatMessage>(response, "Không gửi được ảnh");
}

/** URL ảnh cho <img> phía khách — cookie same-site tự đi kèm request. */
export function customerChatAttachmentUrl(attachmentId: string): string {
  return `${apiBaseUrl}/public/customer-chat/attachments/${attachmentId}`;
}

export function customerChatWsUrl(): string {
  return `${wsBaseUrl()}/public/customer-chat/ws`;
}

// ---- nửa admin (/customer-chats) ---------------------------------------------

async function unwrap<T>(result: { data?: T; error?: unknown }, fallback: string): Promise<T> {
  if (result.error || result.data === undefined) {
    throw new Error(serverErrorMessage(result.error, fallback));
  }
  return result.data;
}

/** Bộ lọc mặc định của hộp thư (bản mẫu #A): thẻ "Tất cả", trạng thái "Đang mở". */
export const DEFAULT_CHAT_FILTERS: ChatListFilters = {
  tag: null,
  tab: "all",
  status: "open",
  unanswered: false,
  mentions: false,
  newCustomer: false,
};

/**
 * Tham số gửi máy chủ. "Nhắc tôi" bật thì ép `tab=all&status=any` (design.md —
 * ô đó là mọi khách có lời nhắc mình chưa xem, không lệ thuộc thẻ đang chọn).
 */
export function listQuery(filters: ChatListFilters) {
  const mentions = filters.mentions;
  return {
    ...(filters.tag ? { tag: filters.tag } : {}),
    tab: mentions ? ("all" as const) : filters.tab,
    status: mentions ? ("any" as const) : filters.status,
    ...(filters.unanswered && !mentions ? { unanswered: true } : {}),
    ...(filters.newCustomer && !mentions ? { newCustomer: true } : {}),
    ...(filters.turn && !mentions ? { turn: filters.turn } : {}),
    ...(mentions ? { mentions: true } : {}),
  };
}

/** Một trang hộp thư. `nextCursor` vắng = đã hết khách. */
export type ChatCustomersPage = { items: ChatAdminCustomer[]; counts: ChatCounts; nextCursor?: string | undefined };

const ZERO_COUNTS: ChatCounts = { mine: 0, unassigned: 0, all: 0, unanswered: 0, mentions: 0, newCustomers: 0 };

/**
 * Hòm thư nhân viên. Trả về KHÁCH, không phải phiên chat — mỗi khách mang sẵn
 * `threads` nên bấm mở luồng không tốn thêm lượt gọi. Kèm `counts` cho ba thẻ
 * và hai ô lọc (QĐ 0223).
 *
 * Mỗi lượt một trang 100 khách; `cursor` là `nextCursor` của trang trước (bỏ
 * trống = trang đầu) và chỉ đúng với CÙNG bộ lọc. Thẻ phân loại lọc ở máy chủ:
 * lọc tại trình duyệt là chỉ lọc được phần đã tải. Bỏ `filters` = danh sách như
 * trước đợt 1.
 */
export async function fetchChatCustomers(
  signal?: AbortSignal,
  filters?: ChatListFilters,
  cursor?: string,
): Promise<ChatCustomersPage> {
  const result = await api.GET("/customer-chats", {
    params: { query: { ...(filters ? listQuery(filters) : {}), ...(cursor ? { cursor } : {}) } },
    signal: signal ?? null,
  });
  const body = await unwrap(result, "Không tải được danh sách chat");
  return { items: body.items ?? [], counts: body.counts ?? ZERO_COUNTS, nextCursor: body.nextCursor };
}

/** Người đang dùng hộp thư (QĐ 0223 §H) — quyết nút nào hiện, có mở socket không. */
export async function fetchChatMe(signal?: AbortSignal): Promise<ChatMe> {
  const result = await api.GET("/customer-chats/me", { signal: signal ?? null });
  return unwrap(result, "Không tải được thông tin người dùng hộp thư");
}

/**
 * Khách chứa một luồng (mọi luồng thấy được của khách đó) — dùng khi mở thẳng
 * một luồng từ đường dẫn chuông báo, hay khi luồng đang mở rơi khỏi bộ lọc.
 */
export async function fetchThreadCustomer(sessionId: string, signal?: AbortSignal): Promise<ChatAdminCustomer> {
  const result = await api.GET("/customer-chats/{id}/customer", {
    params: { path: { id: sessionId } },
    signal: signal ?? null,
  });
  return unwrap(result, "Không mở được cuộc trò chuyện này");
}

/** Trải phẳng thành danh sách luồng — dùng khi cần tra luồng theo id. */
export function flattenThreads(customers: ChatAdminCustomer[]): ChatAdminSession[] {
  return customers.flatMap((customer) => customer.threads);
}

/** Một trang dòng tin (cũ → mới). `nextCursor` vắng = không còn tin nào cũ hơn. */
export type ChatMessagesPage = { items: ChatMessage[]; nextCursor?: string | undefined };

/**
 * Tin của một luồng, đọc NGƯỢC từ tin mới nhất: trang đầu là 200 tin mới nhất;
 * `before` (= `nextCursor` của trang trước) lấy trang tin cũ hơn kế tiếp.
 */
export async function fetchChatMessagesPage(
  sessionId: string,
  before?: string,
  signal?: AbortSignal,
): Promise<ChatMessagesPage> {
  const result = await api.GET("/customer-chats/{id}/messages", {
    params: { path: { id: sessionId }, query: before ? { before } : {} },
    signal: signal ?? null,
  });
  const body = await unwrap(result, "Không tải được tin nhắn");
  return { items: body.items ?? [], nextCursor: body.nextCursor };
}

/**
 * Ghi chú nội bộ (QĐ 0223 §B): `kind: "note"` — khách KHÔNG BAO GIỜ thấy;
 * `mentions` (mã nhân sự) chỉ đi kèm ghi chú. Bỏ trống = tin gửi khách như cũ.
 */
export type AdminSendOptions = { kind?: "message" | "note"; mentions?: string[] };

export async function sendAdminChatMessage(
  sessionId: string,
  body: string,
  replyToId?: string,
  opts?: AdminSendOptions,
): Promise<ChatMessage> {
  const note = opts?.kind === "note";
  const result = await api.POST("/customer-chats/{id}/messages", {
    params: { path: { id: sessionId } },
    body: {
      body,
      ...(replyToId ? { replyToId } : {}),
      ...(note ? { kind: "note" as const } : {}),
      ...(note && opts?.mentions?.length ? { mentions: opts.mentions } : {}),
    },
  });
  return unwrap(result, note ? "Không thêm được ghi chú" : "Không gửi được tin nhắn");
}

export async function sendAdminChatImage(
  sessionId: string,
  file: File,
  caption?: string,
  replyToId?: string,
  opts?: AdminSendOptions,
): Promise<ChatMessage> {
  const note = opts?.kind === "note";
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("body", caption);
  if (replyToId) form.append("replyToId", replyToId);
  if (note) form.append("kind", "note");
  // Cửa nhiều phần nhận danh sách nhắc tên nối bằng dấu phẩy (design.md).
  if (note && opts?.mentions?.length) form.append("mentions", opts.mentions.join(","));
  const response = await postFormAuthed(`${apiBaseUrl}/customer-chats/${sessionId}/attachments`, form);
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      // giữ fallback
    }
    throw new Error(serverErrorMessage(body, "Không gửi được ảnh"));
  }
  return (await response.json()) as ChatMessage;
}

export async function markChatRead(sessionId: string): Promise<ChatAdminSession> {
  const result = await api.POST("/customer-chats/{id}/read", {
    params: { path: { id: sessionId } },
  });
  return unwrap(result, "Không đánh dấu đã đọc được");
}

/**
 * Thu hồi tin mình gửi trong 24 giờ (CEO chốt 2c, QĐ 0340). Mã tin nằm trong THÂN chứ
 * không trong đường dẫn — cùng lý do cửa thẻ ở tiền tố riêng (no-ambiguous-paths).
 */
export async function recallChatMessage(sessionId: string, messageId: string): Promise<ChatMessage> {
  const result = await api.POST("/customer-chats/{id}/recall", {
    params: { path: { id: sessionId } },
    body: { messageId },
  });
  return unwrap(result, "Không thu hồi được tin");
}

// ---- hội thoại mức khách (QĐ 0223 §C–§D) --------------------------------------
// Ba cửa cùng nhận mã LUỒNG (mọi luồng của khách mang chung một khối hội thoại)
// và cùng trả khối mới + dòng hoạt động (null khi không có gì đổi).

/** Nhận (staffId = mình), giao cho người khác, hoặc bỏ phụ trách (null). */
export async function setChatAssignee(sessionId: string, staffId: string | null): Promise<ChatConversationChange> {
  const result = await api.PUT("/customer-chats/{id}/assignee", {
    params: { path: { id: sessionId } },
    body: { staffId },
  });
  return unwrap(result, staffId ? "Không nhận / giao được khách" : "Không bỏ phụ trách được");
}

export async function setChatStatus(
  sessionId: string,
  status: ChatConversationStatus,
  snoozedUntil?: Date,
): Promise<ChatConversationChange> {
  const result = await api.PUT("/customer-chats/{id}/status", {
    params: { path: { id: sessionId } },
    body: { status, ...(status === "snoozed" && snoozedUntil ? { snoozedUntil: snoozedUntil.toISOString() } : {}) },
  });
  return unwrap(result, "Không đổi được trạng thái hội thoại");
}

export async function setChatPriority(sessionId: string, priority: ChatPriority): Promise<ChatConversationChange> {
  const result = await api.PUT("/customer-chats/{id}/priority", {
    params: { path: { id: sessionId } },
    body: { priority },
  });
  return unwrap(result, "Không đổi được mức ưu tiên");
}

/** Người giao được / nhắc được cho luồng này (theo cơ sở của luồng), kèm ai đang mở hộp thư. */
export async function fetchAssignableStaff(sessionId: string, signal?: AbortSignal): Promise<ChatAssignableStaff[]> {
  const result = await api.GET("/customer-chats/{id}/assignable-staff", {
    params: { path: { id: sessionId } },
    signal: signal ?? null,
  });
  const body = await unwrap(result, "Không tải được danh sách đồng nghiệp");
  return body.items ?? [];
}

// ---- khách đang chờ + đơn của khách (QĐ 0271) ---------------------------------

/** Khách của tôi đang chờ + khách chưa ai nhận, trong phạm vi người xem. */
export async function fetchChatWaitingCount(signal?: AbortSignal): Promise<ChatWaitingCount> {
  const result = await api.GET("/customer-chats/waiting-count", { signal: signal ?? null });
  return unwrap(result, "Không tải được số khách đang chờ");
}

/**
 * Mọi đơn của khách đang chat, kể cả đơn trang Đơn hàng không cho người xem
 * thấy — chỉ để trả lời khách ("bao giờ em chụp"), không có tiền.
 */
export async function fetchChatCustomerOrders(sessionId: string, signal?: AbortSignal): Promise<ChatCustomerOrder[]> {
  const result = await api.GET("/customer-chats/{id}/orders", {
    params: { path: { id: sessionId } },
    signal: signal ?? null,
  });
  const body = await unwrap(result, "Không tải được đơn của khách");
  return body.items ?? [];
}

// ---- câu mẫu + giờ làm việc (QĐ 0223 §F–§G) -----------------------------------

export async function fetchCannedReplies(signal?: AbortSignal): Promise<ChatCannedReply[]> {
  const result = await api.GET("/customer-chat-canned-replies", { signal: signal ?? null });
  const body = await unwrap(result, "Không tải được câu mẫu");
  return body.items ?? [];
}

export async function createCannedReply(input: ChatCannedReplyInput): Promise<ChatCannedReply> {
  const result = await api.POST("/customer-chat-canned-replies", { body: input });
  return unwrap(result, "Không thêm được câu mẫu");
}

export async function updateCannedReply(replyId: string, input: ChatCannedReplyInput): Promise<ChatCannedReply> {
  const result = await api.PUT("/customer-chat-canned-replies/{replyId}", {
    params: { path: { replyId } },
    body: input,
  });
  return unwrap(result, "Không sửa được câu mẫu");
}

export async function deleteCannedReply(replyId: string): Promise<void> {
  const result = await api.DELETE("/customer-chat-canned-replies/{replyId}", {
    params: { path: { replyId } },
  });
  await ensureOk(result, "Không xoá được câu mẫu");
}

export async function fetchBusinessHours(
  signal?: AbortSignal,
): Promise<{ items: ChatBusinessHours[]; canEdit: boolean }> {
  const result = await api.GET("/customer-chat-business-hours", { signal: signal ?? null });
  const body = await unwrap(result, "Không tải được giờ làm việc");
  return { items: body.items ?? [], canEdit: Boolean(body.canEdit) };
}

export async function saveBusinessHours(input: ChatBusinessHoursInput): Promise<ChatBusinessHours> {
  const result = await api.PUT("/customer-chat-business-hours", { body: input });
  return unwrap(result, "Không lưu được giờ làm việc");
}

export async function deleteBusinessHours(branchCode: string): Promise<void> {
  const result = await api.DELETE("/customer-chat-business-hours/{branchCode}", {
    params: { path: { branchCode } },
  });
  await ensureOk(result, "Không bỏ được giờ riêng của cơ sở");
}

export function adminChatAttachmentUrl(sessionId: string, attachmentId: string): string {
  return `${apiBaseUrl}/customer-chats/${sessionId}/attachments/${attachmentId}`;
}

export function adminChatWsUrl(): string {
  return `${wsBaseUrl()}/customer-chats/ws`;
}

/** Kiểm tra file trước khi upload — báo lỗi tiếng Việt dễ hiểu. */
export function validateChatImage(file: File): string | null {
  if (!CHAT_IMAGE_TYPES.includes(file.type)) return "Chỉ nhận ảnh JPEG, PNG hoặc WebP.";
  if (file.size > CHAT_MAX_IMAGE_BYTES) return "Ảnh vượt giới hạn 5MB.";
  return null;
}

/** Gộp tin realtime vào danh sách hiện có, không nhân đôi theo id. */
export function appendMessage(items: ChatMessage[] | undefined, incoming: ChatMessage): ChatMessage[] {
  const list = items ?? [];
  if (list.some((item) => item.id === incoming.id)) return list;
  return [...list, incoming];
}

// ---- thẻ phân loại + ghim (quyết định 0135) -----------------------------------
// Danh mục ở /customer-chat-tags (tiền tố riêng, cùng cửa vào trang); gắn thẻ và
// ghim dưới /customer-chats. Gắn/gỡ là hai cửa riêng, idempotent: hai người bấm
// hai thẻ cùng lúc không đè mất nhau. `ensureOk` cho cửa trả 204 (vẫn lấy
// thông điệp máy chủ khi lỗi).
async function ensureOk(result: { error?: unknown }, fallback: string): Promise<void> {
  if (!result.error) return;
  throw new Error(serverErrorMessage(result.error, fallback));
}

export async function fetchChatTags(signal?: AbortSignal): Promise<ChatTag[]> {
  const result = await api.GET("/customer-chat-tags", { signal: signal ?? null });
  const body = await unwrap(result, "Không tải được danh mục thẻ phân loại");
  return body.items ?? [];
}

export async function createChatTag(input: ChatTagInput): Promise<ChatTag> {
  const result = await api.POST("/customer-chat-tags", { body: input });
  return unwrap(result, "Không thêm được thẻ");
}

export async function updateChatTag(tagId: string, input: ChatTagInput): Promise<ChatTag> {
  const result = await api.PUT("/customer-chat-tags/{tagId}", {
    params: { path: { tagId } },
    body: input,
  });
  return unwrap(result, "Không sửa được thẻ");
}

export async function deleteChatTag(tagId: string): Promise<void> {
  const result = await api.DELETE("/customer-chat-tags/{tagId}", {
    params: { path: { tagId } },
  });
  await ensureOk(result, "Không xoá được thẻ");
}

/** Gửi ĐÚNG mọi mã đang có theo thứ tự mới; máy chủ trả 400 nếu thiếu/thừa. */
export async function reorderChatTags(ids: string[]): Promise<ChatTag[]> {
  const result = await api.PUT("/customer-chat-tags", { body: { ids } });
  const body = await unwrap(result, "Không sắp xếp được thẻ");
  return body.items ?? [];
}

/** Gắn (on) / gỡ thẻ cho MỘT luồng; trả luồng mới kèm `tags` để thay nguyên dòng. */
export async function setThreadTag(sessionId: string, tagId: string, on: boolean): Promise<ChatAdminSession> {
  const params = { path: { id: sessionId, tagId } };
  const result = on
    ? await api.POST("/customer-chats/{id}/tags/{tagId}", { params })
    : await api.DELETE("/customer-chats/{id}/tags/{tagId}", { params });
  return unwrap(result, on ? "Không gắn được thẻ" : "Không gỡ được thẻ");
}

/** Gắn / gỡ thẻ cho CẢ KHÁCH; trả danh sách thẻ mức khách sau thao tác. */
export async function setCustomerTag(phone: string, tagId: string, on: boolean): Promise<ChatTag[]> {
  const params = { path: { phone, tagId } };
  const result = on
    ? await api.POST("/customer-chats/customers/{phone}/tags/{tagId}", { params })
    : await api.DELETE("/customer-chats/customers/{phone}/tags/{tagId}", { params });
  const body = await unwrap(result, on ? "Không gắn được thẻ" : "Không gỡ được thẻ");
  return body.items ?? [];
}

/** Ghim / bỏ ghim khách cho RIÊNG mình (đồng nghiệp không thấy ghim của bạn). */
export async function setCustomerPin(phone: string, on: boolean): Promise<void> {
  const result = on
    ? await api.POST("/customer-chats/customers/{phone}/pin", { params: { path: { phone } } })
    : await api.DELETE("/customer-chats/customers/{phone}/pin", { params: { path: { phone } } });
  await ensureOk(result, on ? "Không ghim được" : "Không bỏ ghim được");
}

// ---- mục "Người giới thiệu & đối tác" (/affiliate/chats, quyền affiliate.chat) ----
// Cùng kho tin nhắn với chat khách nhưng KHÁC cửa: cửa này chỉ mở phiên của
// người có mã giới thiệu. Không có WebSocket — mục này tải lại theo chu kỳ.

export async function fetchPartnerChatSessions(signal?: AbortSignal): Promise<ChatPartnerSession[]> {
  const result = await api.GET("/affiliate/chats", { signal: signal ?? null });
  const body = await unwrap(result, "Không tải được danh sách chat đối tác");
  return body.items ?? [];
}

export async function fetchPartnerChatMessages(sessionId: string, signal?: AbortSignal): Promise<ChatMessage[]> {
  const result = await api.GET("/affiliate/chats/{sessionId}/messages", {
    params: { path: { sessionId } },
    signal: signal ?? null,
  });
  const body = await unwrap(result, "Không tải được tin nhắn");
  return body.items ?? [];
}

export async function sendPartnerChatMessage(sessionId: string, body: string): Promise<ChatMessage> {
  const result = await api.POST("/affiliate/chats/{sessionId}/messages", {
    params: { path: { sessionId } },
    body: { body },
  });
  return unwrap(result, "Không gửi được tin nhắn");
}

export async function sendPartnerChatImage(sessionId: string, file: File, caption?: string): Promise<ChatMessage> {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("body", caption);
  const response = await postFormAuthed(`${apiBaseUrl}/affiliate/chats/${sessionId}/attachments`, form);
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      // giữ fallback
    }
    throw new Error(serverErrorMessage(body, "Không gửi được ảnh"));
  }
  return (await response.json()) as ChatMessage;
}

export async function markPartnerChatRead(sessionId: string): Promise<ChatPartnerSession> {
  const result = await api.POST("/affiliate/chats/{sessionId}/read", {
    params: { path: { sessionId } },
  });
  return unwrap(result, "Không đánh dấu đã đọc được");
}

export function partnerChatAttachmentUrl(sessionId: string, attachmentId: string): string {
  return `${apiBaseUrl}/affiliate/chats/${sessionId}/attachments/${attachmentId}`;
}

// Kiểu khung chat phía khách ("ô cửa sổ hoa leo", CEO chốt 28/09/2026). Ai vào
// hộp thư cũng xem; chỉ trưởng CSKH và quản lý lưu — máy chủ nói qua `canEdit`.
export async function fetchChatAppearance(signal?: AbortSignal): Promise<ChatAppearanceAdmin> {
  const result = await api.GET("/customer-chat-appearance", { signal: signal ?? null });
  return unwrap(result, "Không tải được giao diện khung chat");
}

export async function saveChatAppearance(input: ChatAppearance): Promise<ChatAppearanceAdmin> {
  const result = await api.PUT("/customer-chat-appearance", { body: input });
  return unwrap(result, "Không lưu được giao diện khung chat");
}
